/**
 * FeatureCard — a single feature row: toggle switch + name/description +
 * status badges (whitelisted / hidden) + whitelist controls (star + solo).
 *
 * Extracted from FeatureManagerWindow; behavior-preserving. The toggle is a
 * bespoke switch (no primitive equivalent) kept on theme tokens.
 */
import React from 'react';
import {
  COLORS,
  SPACE,
  RADII,
  FONT_SIZE,
  FONT_WEIGHT,
  SHADOW,
  DURATION,
  EASING,
} from '../../theme';
import { Badge } from '../../primitives';
import type { FeatureConfig } from '../../../../core/features';

export interface FeatureCardProps {
  name: string;
  config: FeatureConfig;
  isEnabled: boolean;
  isWhitelisted: boolean;
  isVisible: boolean;
  /** Dim the card (NORMAL mode + disabled feature). */
  dimmed: boolean;
  onToggle: () => void;
  onWhitelistToggle: () => void;
  onSolo: () => void;
}

function cardStyle(isWhitelisted: boolean, dimmed: boolean): React.CSSProperties {
  return {
    padding: `${SPACE.lg + SPACE.xxs}px ${SPACE.xl}px`,
    marginBottom: SPACE.md,
    borderRadius: RADII.lg,
    background: isWhitelisted ? `${COLORS.warning}0d` : COLORS.borderFaint,
    border: `1px solid ${isWhitelisted ? `${COLORS.warning}4d` : COLORS.borderFaint}`,
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.lg + SPACE.xxs,
    opacity: dimmed ? 0.5 : 1,
    transition: `all ${DURATION.fast} ${EASING.standard}`,
  };
}

function toggleStyle(isEnabled: boolean): React.CSSProperties {
  return {
    width: 44,
    height: 24,
    borderRadius: RADII.xl,
    position: 'relative',
    cursor: 'pointer',
    transition: `background ${DURATION.base} ${EASING.standard}`,
    flexShrink: 0,
    background: isEnabled ? COLORS.success : COLORS.textMuted,
  };
}

function toggleKnobStyle(isEnabled: boolean): React.CSSProperties {
  return {
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: COLORS.textPrimary,
    position: 'absolute',
    top: 2,
    left: isEnabled ? 22 : 2,
    transition: `left ${DURATION.base} ${EASING.standard}`,
    boxShadow: SHADOW.sm,
  };
}

const infoStyle: React.CSSProperties = { flex: 1 };

const nameStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.lg,
  fontWeight: FONT_WEIGHT.medium,
  color: COLORS.textPrimary,
  marginBottom: SPACE.xxs,
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.md,
};

const descStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.md,
  color: COLORS.textMuted,
};

const controlsStyle: React.CSSProperties = {
  display: 'flex',
  gap: SPACE.sm,
};

function whitelistButtonStyle(active: boolean): React.CSSProperties {
  return {
    padding: `${SPACE.xs}px ${SPACE.md}px`,
    borderRadius: RADII.md,
    border: 'none',
    background: active ? `${COLORS.warning}4d` : COLORS.surfaceHover,
    color: active ? COLORS.warning : COLORS.textSecondary,
    fontSize: FONT_SIZE.sm,
    cursor: 'pointer',
  };
}

export const FeatureCard: React.FC<FeatureCardProps> = ({
  name,
  config,
  isEnabled,
  isWhitelisted,
  isVisible,
  dimmed,
  onToggle,
  onWhitelistToggle,
  onSolo,
}) => {
  return (
    <div style={cardStyle(isWhitelisted, dimmed)}>
      {/* Toggle Switch */}
      <div style={toggleStyle(isEnabled)} onClick={onToggle}>
        <div style={toggleKnobStyle(isEnabled)} />
      </div>

      {/* Feature Info */}
      <div style={infoStyle}>
        <div style={nameStyle}>
          {name}
          {isWhitelisted && <Badge tone="warning">whitelisted</Badge>}
          {!isVisible && isEnabled && <Badge tone="danger">hidden</Badge>}
        </div>
        {config.description && <div style={descStyle}>{config.description}</div>}
      </div>

      {/* Whitelist Controls */}
      <div style={controlsStyle}>
        <button
          type="button"
          style={whitelistButtonStyle(isWhitelisted)}
          onClick={onWhitelistToggle}
          title="Add/remove from whitelist"
        >
          {isWhitelisted ? '★' : '☆'}
        </button>
        <button
          type="button"
          style={whitelistButtonStyle(false)}
          onClick={onSolo}
          title="Show only this feature"
        >
          solo
        </button>
      </div>
    </div>
  );
};
